import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const services = [
  {
    icon: 'fa-solid fa-database',
    title: '1. Enterprise Data Lake & Lakehouse Setup',
    desc: 'Architect high-capacity, scalable data lakes and lakehouses using platforms like Databricks and Snowflake to store structured and unstructured multi-source data.',
  },
  {
    icon: 'fa-solid fa-chart-line',
    title: '2. Predictive Analytics & Forecasting',
    desc: 'Deploy advanced statistical models and Machine Learning algorithms to anticipate market trends, operational risks, and customer behavioral patterns.',
  },
  {
    icon: 'fa-solid fa-bolt',
    title: '3. Real-Time Data Streaming & Processing',
    desc: 'Process continuous streams of high-velocity data using Apache Spark and Kafka to power instant operational alerts and live executive dashboards.',
  },
  {
    icon: 'fa-solid fa-chart-pie',
    title: '4. Business Intelligence & Custom Dashboards',
    desc: 'Transform raw enterprise metrics into intuitive, interactive BI visualizations on platforms like Power BI, Tableau, and Looker.',
  },
  {
    icon: 'fa-solid fa-shield-halved',
    title: '5. Data Governance & Metadata Management',
    desc: 'Implement automated metadata tagging, role-based access controls, lineage tracking, and compliance frameworks for GDPR, HIPAA, and SOC2.',
  },
  {
    icon: 'fa-solid fa-network-wired',
    title: '6. ETL & Automated Data Pipelines',
    desc: 'Build batch and real-time data pipelines using Apache Airflow and dbt to cleanse, aggregate, and stage raw inputs for immediate querying.',
  },
  {
    icon: 'fa-solid fa-brain',
    title: '7. Advanced Machine Learning Analytics',
    desc: 'Leverage deep learning and predictive modeling to discover hidden correlations, optimize supply pricing, and automate decisions at scale.',
  },
  {
    icon: 'fa-solid fa-diagram-project',
    title: '8. Data Architecture Modernization',
    desc: 'Migrate on-premises legacy data warehouses to modern, cost-efficient cloud architectures designed for petabyte-scale throughput.',
  },
  {
    icon: 'fa-solid fa-magnifying-glass-chart',
    title: '9. Customer & Behavioral Analytics',
    desc: 'Consolidate touchpoint data to construct 360-degree customer profiles, enable micro-segmentation, and reduce attrition rates.',
  },
  {
    icon: 'fa-solid fa-gauge-high',
    title: '10. Performance Optimization & Query Tuning',
    desc: 'Optimize database schemas, partition strategies, and SQL query efficiency to reduce cloud computing costs and improve load times.',
  },
];

const whyChooseUs = [
  'Petabyte-scale cloud data lake architecture expertise',
  'Real-time event processing and streaming capabilities',
  'Advanced predictive modeling and ML integration',
  'Strict governance, lineage tracking, and HIPAA/GDPR compliance',
  'Cost-optimized query performance and warehouse management',
  'Custom BI dashboards tailored to C-suite decision making',
  'Unified single source of truth for cross-departmental data',
  'Seamless integration with modern SaaS, legacy, and IoT sources',
  'Automated CI/CD pipelines for data engineering (DataOps)',
  '24/7 telemetry monitoring and pipeline health maintenance',
];

const processSteps = [
  { step: '01', title: 'Data Discovery & Audit', desc: 'Identify data sources, velocity requirements, schema complexity, and core business questions.' },
  { step: '02', title: 'Architecture Blueprinting', desc: 'Design scalable target lakehouses, data pipelines, and governance policies.' },
  { step: '03', title: 'Pipeline & Ingestion Engineering', desc: 'Develop automated batch and streaming ETL/ELT pipelines with validation layers.' },
  { step: '04', title: 'Analytics & Model Development', desc: 'Build predictive ML models, custom data marts, and interactive BI reporting views.' },
  { step: '05', title: 'Governance & Security Rollout', desc: 'Enforce dataset encryption, granular RBAC access, and automated lineage tracking.' },
  { step: '06', title: 'Monitoring & Optimization', desc: 'Track query speeds, compute costs, and pipeline performance with continuous refinement.' },
];

const techStack = [
  { category: 'Data Warehousing & Lakes', items: ['Snowflake', 'Databricks', 'Google BigQuery', 'AWS Redshift', 'Azure Synapse'] },
  { category: 'Processing & Orchestration', items: ['Apache Spark', 'Apache Kafka', 'Apache Airflow', 'dbt', 'AWS Glue'] },
  { category: 'Business Intelligence & Viz', items: ['Power BI', 'Tableau', 'Looker', 'Grafana', 'Metabase'] },
  { category: 'Data Governance & Storage', items: ['Collibra', 'Apache Atlas', 'AWS S3', 'Azure Blob', 'PostgreSQL'] },
];

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const BigDataAnalytics = () => {
  return (
    <div className="service-page text-white overflow-hidden">
      {/* 1. HERO SECTION */}
      <section className="hero-sub-section text-center d-flex align-items-center justify-content-center">
        <div className="container py-5">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge rounded-pill bg-outline-cyan px-3 py-2 mb-3 glow-badge">
              Lidlbritain Digital Transformation
            </span>
            <h1 className="display-4 fw-bold mb-3">
              Big Data & <span className="gradient-text-cyan">Analytics</span>
            </h1>
            <p className="lead mx-auto opacity-75 max-width-800">
              At <strong>Lidlbritain</strong>, we turn complex enterprise data into a decisive operational advantage. Build petabyte-scale data lakehouses, deploy automated pipelines, and leverage predictive analytics to drive growth.
            </p>
            <div className="mt-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
                <Link to="/contact" className="btn gradient-btn px-4 py-2 rounded-pill">
                  Unlock Your Data Potential &rarr;
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. SERVICES GRID SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              Big Data & Analytics Solutions by <span className="gradient-text-orange">Lidlbritain</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {services.map((item, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  className="service-card p-4 rounded-4 h-100 border border-secondary border-opacity-25 bg-glass"
                >
                  <div className="icon-box mb-3 d-flex align-items-center justify-content-center floating-icon">
                    <i className={`${item.icon} fs-3 text-cyan`}></i>
                  </div>
                  <h5 className="fw-bold mb-3">{item.title}</h5>
                  <p className="text-light opacity-75 small leading-relaxed">{item.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 3. WHY CHOOSE US & TECH STACK SECTION */}
      <section className="py-5 bg-dark-overlay">
        <div className="container">
          <div className="row g-5 align-items-center">
            {/* Why Choose Lidlbritain */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="fw-bold mb-4">
                Why Partner With <span className="gradient-text-cyan">Lidlbritain?</span>
              </h3>
              <div className="row g-3">
                {whyChooseUs.map((reason, idx) => (
                  <div key={idx} className="col-sm-6">
                    <div className="d-flex align-items-center gap-2">
                      <i className="fa-solid fa-circle-check text-orange pulse-icon"></i>
                      <span className="small opacity-90">{reason}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Tools & Tech Stack */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="p-4 rounded-4 border border-secondary border-opacity-25 bg-glass glow-box">
                <h4 className="fw-bold mb-4">Big Data Tech Ecosystem</h4>
                {techStack.map((tech, idx) => (
                  <div key={idx} className="mb-3">
                    <h6 className="text-cyan mb-2 fw-semibold">{tech.category}</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {tech.items.map((item, i) => (
                        <motion.span
                          whileHover={{ scale: 1.1 }}
                          key={i}
                          className="badge bg-secondary bg-opacity-22 border border-secondary border-opacity-50 text-light fw-normal py-2 px-3"
                        >
                          {item}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. PROCESS SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              The Lidlbritain <span className="gradient-text-orange">Data Engineering Model</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {processSteps.map((stepItem, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ scale: 1.03 }}
                  className="process-card p-4 rounded-4 h-100 bg-glass border border-secondary border-opacity-25 position-relative"
                >
                  <span className="step-number fw-bold text-cyan mb-2 d-block">{stepItem.step}</span>
                  <h5 className="fw-bold mb-2">{stepItem.title}</h5>
                  <p className="text-light opacity-75 small mb-0">{stepItem.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 5. CALL TO ACTION SECTION */}
      <section className="py-5 my-4">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-5 rounded-5 cta-banner text-center position-relative overflow-hidden"
          >
            <h2 className="fw-bold mb-3">Scale Your Enterprise Analytics with Lidlbritain</h2>
            <p className="lead opacity-90 mx-auto mb-4 max-width-800">
              Transform raw data silos into automated pipelines and predictive insights engineered for speed and decision clarity.
            </p>
            <motion.div whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
              <Link to="/contact" className="btn gradient-btn btn-lg px-5 py-3 rounded-pill fw-semibold">
                Consult with a Big Data Architect &rarr;
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BigDataAnalytics;